import axios from "axios";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "../components";
import { CloseIcon } from "../components/Icons";

declare module "axios" {
  export interface AxiosRequestConfig {
    hideToast?: boolean;
    errorMessage?: string;
    // The shared `errors.<code>` dictionary is keyed by one service's exception
    // codes. Other services reuse the same numeric range, so their codes resolve
    // to an unrelated message — which would also reach Sentry and poison the
    // fingerprint. Set this on requests to such an endpoint: the server-supplied
    // message is then used verbatim instead of the dictionary lookup.
    skipSharedErrorDictionary?: boolean;
  }
}

const useGlobalErrorHandler = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error?.response?.status !== 401 &&
          error?.response?.status !== 403
        ) {
          const fallbackMessage =
            error.response?.data?.message ||
            t(error.config?.errorMessage, { defaultValue: "" }) ||
            error.message;

          const errorMessage = error.config?.skipSharedErrorDictionary
            ? fallbackMessage
            : t(`errors.${error.response?.data?.errorCode}`, {
                defaultValue: fallbackMessage,
              });

          if (!error.config.hideToast) {
            showNotification(errorMessage, undefined, CloseIcon);
          }

          Sentry.withScope((scope) => {
            scope.setFingerprint(
              [
                errorMessage,
                error?.config?.url,
                error?.response?.status?.toString(),
                error.response?.data?.errorCode?.toString(),
                error?.response?.data?.additionalInfo?.[0]?.code,
              ].filter(Boolean)
            );
            Sentry.captureException(error, {
              extra: {
                response: error?.response?.data,
                errorMessage,
                additionalInfo: error?.response?.data?.additionalInfo,
              },
            });
          });
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [t]);
};

export default useGlobalErrorHandler;
